import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import SettingsPopout from '../shared/SettingsPopout';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import { SGTooltip } from '../core';
import { MuiColorInput } from 'mui-color-input';
import { TraceStrikeBarType } from '../../types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TraceFiltersReadable } from '../../config';
import { isValidHex } from '../../util';
import useToast from '../../hooks/useToast';
import { useTraceCtx } from './TraceContext';

export const TraceColorSettings = () => {
  const {
    config,
    updateConfig,
    selectedLense,
    selectedTheme,
    heatmapColorSettings,
  } = useTraceCtx();
  const strikeBarType = config.strikeBarType;
  const { openToast } = useToast();

  const applyPatch = (
    patch: Parameters<typeof updateConfig>[0],
    value?: string,
  ) => {
    if (value != null && !isValidHex(value)) {
      openToast({
        message: 'Invalid color supplied. Please try again with a valid color.',
        type: 'error',
      });
      return;
    }
    updateConfig(patch);
  };

  const customColorsForSelectedLense =
    config.customColorsPerLense?.[selectedLense];

  const resetCustomLenseColors = () =>
    applyPatch({
      customColorsPerLense: {
        ...(config.customColorsPerLense ?? {}),
        [selectedLense]: undefined,
      },
    });

  const resetAllColors = () => {
    applyPatch({
      customColorsPerLense: {
        ...(config.customColorsPerLense ?? {}),
        [selectedLense]: undefined,
      },
      hiroColor: undefined,
      posColor: undefined,
      negColor: undefined,
      posStrikeColor: undefined,
      negStrikeColor: undefined,
      downCandleLineColor: undefined,
      upCandleLineColor: undefined,
      upCandleFill: undefined,
      downCandleFill: undefined,
      putColor: undefined,
      callColor: undefined,
      contourLineColor: undefined,
    });
  };

  return (
    <Box sx={{ height: '40px', alignSelf: 'center' }}>
      <SettingsPopout
        title="TRACE Color Settings"
        popperID={'trace-color-settings'}
        zIndex={2}
        placement="left-start"
        tooltipPlacement="left"
        icon={<InvertColorsIcon />}
      >
        <Stack direction="row">
          <Box display="flex" flexDirection="column" gap="6px">
            <SGTooltip
              placement={'top'}
              title={
                'This color is used as the default color for positive values on the heatmap'
              }
            >
              <Typography variant="h4" gutterBottom color="primary">
                Positive Heatmap Color
              </Typography>
            </SGTooltip>

            <MuiColorInput
              format="hex8"
              value={
                config.posColor ??
                selectedTheme.palette.trace.heatmapSettings.posHeatmapColor
              }
              onChange={(value: string) =>
                applyPatch({ posColor: value }, value)
              }
              PopoverProps={{
                anchorOrigin: {
                  horizontal: 'left',
                  vertical: 'bottom',
                },
              }}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap="6px">
            <SGTooltip
              placement={'top'}
              title={
                'This color is used as the default color for negative values on the heatmap'
              }
            >
              <Typography variant="h4" gutterBottom color="primary">
                Negative Heatmap Color
              </Typography>
            </SGTooltip>
            <MuiColorInput
              format="hex8"
              value={
                config.negColor ??
                selectedTheme.palette.trace.heatmapSettings.negHeatmapColor
              }
              onChange={(value: string) =>
                applyPatch({ negColor: value }, value)
              }
              PopoverProps={{
                anchorOrigin: {
                  horizontal: 'right',
                  vertical: 'bottom',
                },
              }}
            />
          </Box>
        </Stack>

        {strikeBarType !== TraceStrikeBarType.NONE && (
          <Accordion sx={{ marginY: '3px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              <Typography>
                <h4>Strike Plot Color Settings</h4>
              </Typography>
            </AccordionSummary>

            <Stack direction="row">
              <Box display="flex" flexDirection="column" gap="6px">
                <SGTooltip
                  placement={'left'}
                  title={`This color is used as the default color for positive values on the strike plot when GEX is selected`}
                >
                  <Typography variant="h4" gutterBottom color="primary">
                    Positive GEX Color
                  </Typography>
                </SGTooltip>

                <MuiColorInput
                  format="hex8"
                  value={heatmapColorSettings.posStrikeColor}
                  onChange={(value: string) =>
                    applyPatch({ posStrikeColor: value }, value)
                  }
                  PopoverProps={{
                    anchorOrigin: {
                      horizontal: 'left',
                      vertical: 'bottom',
                    },
                  }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="6px">
                <SGTooltip
                  placement={'left'}
                  title={`This color is used as the default color for negative values on the strike bars when GEX is selected`}
                >
                  <Typography variant="h4" gutterBottom color="primary">
                    Negative GEX Color
                  </Typography>
                </SGTooltip>
                <MuiColorInput
                  format="hex8"
                  value={heatmapColorSettings.negStrikeColor}
                  onChange={(value: string) =>
                    applyPatch({ negStrikeColor: value }, value)
                  }
                  PopoverProps={{
                    anchorOrigin: {
                      horizontal: 'right',
                      vertical: 'bottom',
                    },
                  }}
                />
              </Box>
            </Stack>

            <Stack direction="row">
              <Box display="flex" flexDirection="column" gap="6px">
                <SGTooltip
                  placement={'left'}
                  title={`This color is used as the default color for call values on the strike plot when OI or Net OI are selected`}
                >
                  <Typography variant="h4" gutterBottom color="primary">
                    OI Calls Color
                  </Typography>
                </SGTooltip>

                <MuiColorInput
                  format="hex8"
                  value={heatmapColorSettings.callColor}
                  onChange={(value: string) =>
                    applyPatch({ callColor: value }, value)
                  }
                  PopoverProps={{
                    anchorOrigin: {
                      horizontal: 'left',
                      vertical: 'bottom',
                    },
                  }}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="6px">
                <SGTooltip
                  placement={'left'}
                  title={`This color is used as the default color for put values on the strike bars when OI or Net OI are selected`}
                >
                  <Typography variant="h4" gutterBottom color="primary">
                    OI Puts Color
                  </Typography>
                </SGTooltip>
                <MuiColorInput
                  format="hex8"
                  value={heatmapColorSettings.putColor}
                  onChange={(value: string) =>
                    applyPatch({ putColor: value }, value)
                  }
                  PopoverProps={{
                    anchorOrigin: {
                      horizontal: 'right',
                      vertical: 'bottom',
                    },
                  }}
                />
              </Box>
            </Stack>
          </Accordion>
        )}

        <Accordion sx={{ marginY: '3px !important' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
            <Typography>
              <h4>HIRO and Contour Color Settings</h4>
            </Typography>
          </AccordionSummary>
          <Stack direction="row">
            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                title={'The color of the HIRO line on the heatmap'}
                placement="left"
              >
                <Typography variant="h4" gutterBottom color="primary">
                  HIRO Line Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.hiroColor}
                onChange={(value: string) =>
                  applyPatch({ hiroColor: value }, value)
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'left',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                title={'The color of the contour lines on the heatmap'}
                placement="left"
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Contour Lines Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.contourLineColor}
                onChange={(value: string) =>
                  applyPatch({ contourLineColor: value }, value)
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'left',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>
          </Stack>
        </Accordion>

        <Accordion sx={{ marginY: '3px !important' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
            <Typography>
              <h4>Candle Color Settings</h4>
            </Typography>
          </AccordionSummary>
          <Stack direction="row">
            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                title={
                  'The color of the borders of up/positive price candles on the heatmap'
                }
                placement="left"
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Up Candle Border Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.candleUpLineColor}
                onChange={(value: string) =>
                  applyPatch({ upCandleLineColor: value }, value)
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'left',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                title={
                  'The color of the borders of down/negative price candles on the heatmap'
                }
                placement="left"
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Down Candle Border Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.candleDownLineColor}
                onChange={(value: string) =>
                  applyPatch({ downCandleLineColor: value }, value)
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>
          </Stack>

          <Stack direction="row">
            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                title={
                  'The color of the inside body of up/positive price candles on the heatmap'
                }
                placement="left"
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Up Candle Fill Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.candleUpFillColor}
                onChange={(value: string) =>
                  applyPatch({ upCandleFill: value }, value)
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'left',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                title={
                  'The color of the inside body of down/negative price candles on the heatmap'
                }
                placement="left"
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Down Candle Fill Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.candleDownFillColor}
                onChange={(value: string) =>
                  applyPatch({ downCandleFill: value }, value)
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>
          </Stack>
        </Accordion>

        <Accordion
          sx={{ marginTop: '3px !important', marginBottom: '10px !important' }}
          defaultExpanded={customColorsForSelectedLense != null}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
            <SGTooltip
              placement="left"
              title={`Set colors that only apply when you have ${TraceFiltersReadable.get(
                selectedLense,
              )!} selected`}
            >
              <Typography>
                <h4>
                  Colors For {TraceFiltersReadable.get(selectedLense)!} Only
                </h4>
              </Typography>
            </SGTooltip>
          </AccordionSummary>

          <Stack direction="row">
            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                placement={'left'}
                title={`This will override your default positive heatmap color, but only when ${TraceFiltersReadable.get(
                  selectedLense,
                )!} is selected and only for the heatmap.`}
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Positive Heatmap Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.posHeatmapColor}
                onChange={(value: string) =>
                  applyPatch(
                    {
                      customColorsPerLense: {
                        ...(config.customColorsPerLense ?? {}),
                        [selectedLense]: {
                          ...(customColorsForSelectedLense ?? {}),
                          posHeatmapColor: value,
                        },
                      },
                    },
                    value,
                  )
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'left',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="6px">
              <SGTooltip
                placement={'left'}
                title={`This will override your default negative heatmap color, but only when ${TraceFiltersReadable.get(
                  selectedLense,
                )!} is selected and only for the heatmap.`}
              >
                <Typography variant="h4" gutterBottom color="primary">
                  Negative Heatmap Color
                </Typography>
              </SGTooltip>
              <MuiColorInput
                format="hex8"
                value={heatmapColorSettings.negHeatmapColor}
                onChange={(value: string) =>
                  applyPatch(
                    {
                      customColorsPerLense: {
                        ...(config.customColorsPerLense ?? {}),
                        [selectedLense]: {
                          ...(customColorsForSelectedLense ?? {}),
                          negHeatmapColor: value,
                        },
                      },
                    },
                    value,
                  )
                }
                PopoverProps={{
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom',
                  },
                }}
              />
            </Box>
          </Stack>
        </Accordion>

        <Stack direction="row" justifyContent="space-between">
          <SGTooltip
            placement="bottom"
            title={'Reset all color settings to their SpotGamma defaults'}
          >
            <Button
              onClick={resetAllColors}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
              }}
            >
              Reset All
            </Button>
          </SGTooltip>

          {customColorsForSelectedLense != null && (
            <SGTooltip
              placement="bottom"
              title={`Remove the custom colors specified under 'Colors for ${TraceFiltersReadable.get(
                selectedLense,
              )!} only'`}
            >
              <Button
                onClick={resetCustomLenseColors}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                }}
              >
                Reset {TraceFiltersReadable.get(selectedLense)!} Colors
              </Button>
            </SGTooltip>
          )}
        </Stack>
      </SettingsPopout>
    </Box>
  );
};
